import React from 'react';
import { Helmet } from 'react-helmet-async';
import HeroSection from '../components/HeroSection';
import BrandCard   from '../components/BrandCard';
import CTASection  from '../components/CTASection';
import Reveal      from '../components/Reveal';

/* -------------------------------------------------------
   PORTFOLIO DATA
   ------------------------------------------------------- */
const BRANDS = [
  {
    category:    'AI Communications Platform',
    name:        'DriveTalkAI',
    logo:        '/images/logos/drivetalkai.png',
    logoBg:      'dark',
    url:         'drivetalkai.com',
    href:        'https://drivetalkai.com',
    description:
      'An AI-powered, multi-channel communications company integrating Voice, SMS, Chat, Messenger, and Email into a unified, automated platform. Using Conversational AI, Natural Language Processing (NLP), and Machine Learning, these solutions deliver, handle, and analyze interactions in real-time to enhance customer experience (CX), reduce operational costs, and automate repetitive tasks.',
    valueProps: [
      'Unified Voice, SMS, Chat, Messenger, and Email automation in a single platform',
      'Conversational AI and NLP for real-time interaction handling and analysis',
      'Measurable reduction in operational costs through intelligent task automation',
      'Designed to enhance CX at scale without proportional headcount growth',
    ],
  },
  {
    category:    'Health & Beauty Commerce',
    name:        'AddyMay',
    logo:        '/images/logos/addymay.png',
    logoBg:      'white',
    url:         'addymay.com',
    href:        'https://addymay.com',
    description:
      'A company focused on high-end electronic consumer products for women\'s health and beauty, operating at the intersection of luxury skincare, advanced technology, and wellness. AddyMay is characterized by sleek design, clinical-grade efficacy, and premium price points, aiming to deliver spa- or dermatologist-level results at home.',
    valueProps: [
      'Premium electronic beauty and wellness devices positioned at the luxury tier',
      'Clinical-grade efficacy with consumer-accessible delivery and experience',
      'Operates at the intersection of skincare technology, design, and wellness',
      'High-AOV product model with strong brand loyalty and repeat purchase potential',
    ],
  },
  {
    category:    'Performance Marketing & Events',
    name:        'Driven2Win',
    logo:        '/images/logos/driven2win.jpg',
    logoBg:      'white',
    url:         'www.driven2win.com',
    href:        'http://www.driven2win.com',
    description:
      'A company focused on driving revenue through performance marketing and events, acting as a revenue-centric growth partner rather than a traditional branding agency. The primary goal is to turn marketing investment into measurable revenue — not merely brand visibility. Driven2Win holds itself accountable for lead generation, customer acquisition, and closed-won deals.',
    valueProps: [
      'Revenue-first performance marketing with accountability for measurable business outcomes',
      'Blurs the line between marketing and sales to drive lead generation and acquisition',
      'Event-driven demand generation strategies that convert attendance into pipeline',
      'Structured as a growth partner, not a traditional agency — outcomes over impressions',
    ],
  },
  {
    category:    'E-Commerce & Marketplace',
    name:        'PIC and SAV',
    logo:        '/images/logos/picandsav.png',
    logoBg:      'white',
    url:         'PICandSAV.com',
    href:        'https://PICandSAV.com',
    description:
      'A multi-channel e-commerce seller and brand accelerator operating across Amazon, eBay, and other established marketplaces. This business model centers on diversifying sales channels to reduce risk, maximize reach, and leverage the high-traffic infrastructure of established platforms — rather than relying on a single, standalone storefront.',
    valueProps: [
      'Multi-channel marketplace presence across Amazon, eBay, and beyond',
      'Channel diversification strategy that reduces single-platform dependency risk',
      'Leverages established marketplace traffic for maximum product reach and velocity',
      'Brand accelerator model focused on scaling seller performance across platforms',
    ],
  },
];

/* -------------------------------------------------------
   SHARED SERVICES (displayed in this section)
   ------------------------------------------------------- */
const SHARED_SERVICES = [
  { title: 'Finance & Accounting',     body: 'Consolidated financial reporting, budgeting, and accounting support across all portfolio brands.' },
  { title: 'Legal & Compliance',       body: 'Centralized legal oversight, contract review, intellectual property management, and regulatory compliance.' },
  { title: 'Technology Infrastructure', body: 'Shared hosting, security protocols, data management, and technology stack governance.' },
  { title: 'Marketing Operations',     body: 'Cross-brand marketing guidance, brand standards, asset production support, and performance analysis.' },
  { title: 'HR & Talent',              body: 'Contractor and team management, onboarding, policy administration, and talent development support.' },
  { title: 'Analytics & Reporting',    body: 'Standardized KPI frameworks, performance dashboards, and data infrastructure supporting portfolio-level visibility.' },
];

export default function Brands() {
  return (
    <>
      <Helmet>
        <title>Portfolio — Driven 2 LLC Digital Brand Portfolio</title>
        <meta
          name="description"
          content="Driven 2 LLC owns and operates a portfolio of digital brands including DriveTalkAI, AddyMay, Driven2Win, and PIC and SAV. Learn about each brand and the shared services that support them."
        />
        <meta property="og:title" content="Portfolio — Driven 2 LLC Digital Brand Portfolio" />
        <meta property="og:description" content="A disciplined portfolio of digital brands — AI communications, digital media, performance content, and consumer savings — operated under centralized governance." />
        <meta property="og:url" content="https://www.driven2.net/brands" />
        <link rel="canonical" href="https://www.driven2.net/brands" />
      </Helmet>

      {/*
        ═══════════════════════════════════════════
        HERO PLACEHOLDER DIRECTION:
        "Multi-brand portfolio visualization —
        premium digital screens arranged in a
        structured layout, strategic brand
        architecture presentation feel,
        modern corporate investment style.
        Deep navy tones with gold accent light."

        TO REPLACE: <HeroSection bgImage="/images/hero-brands.jpg" ... />
        ═══════════════════════════════════════════
      */}
      <HeroSection
        eyebrow="Brand Portfolio"
        title={"A Focused Portfolio of\nDigital Brands"}
        subtitle="One governance framework. Centralized shared services and disciplined operational oversight that allows each brand to focus entirely on creating value for its audience."
        ctaPrimary={{ label: 'View Operating Model', to: '/operating-model' }}
        size="medium"
        bgImage="https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&w=1920&q=80"
      />

      {/* ═══════════════════════════════════════
          PORTFOLIO OVERVIEW
          ═══════════════════════════════════════ */}
      <section className="section section--alt">
        <div className="container">
          <Reveal>
            <div style={{ maxWidth: '820px' }}>
              <div className="eyebrow">Portfolio Overview</div>
              <h2 className="d-lg t-navy mb-8">
                How We Build and Manage Our Brands
              </h2>
              <p className="lead mb-6">
                The Driven 2 LLC portfolio is built on a simple premise: focused digital
                brands with strong fundamentals, operated under institutional governance,
                perform better over the long term than underdisciplined operations of any
                size. Each brand in our portfolio is chosen for its addressable market,
                operating potential, and fit within our shared services model.
              </p>
              <p className="lead">
                Central to our approach is the belief that governance infrastructure —
                finance, legal, technology, analytics — should not be a tax on individual
                brand budgets. By centralizing these functions at the holding company level,
                we eliminate operational redundancy while ensuring every brand operates
                within a consistent, professional framework.
              </p>
            </div>
          </Reveal>

          <div style={{ marginTop: 'var(--sp-12)', display: 'flex', gap: 'var(--sp-12)', flexWrap: 'wrap' }}>
            {[
              { num: 'FL',          label: 'Holding Company Base'     },
              { num: '6',           label: 'Shared Service Functions' },
              { num: 'Multi-Channel', label: 'Market Reach'           },
              { num: 'Long-Horizon', label: 'Ownership Approach'      },
            ].map(({ num, label }) => (
              <Reveal key={label}>
                <div className="stat">
                  <div className="stat__num">{num}</div>
                  <div className="stat__label">{label}</div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════
          BRAND CARDS
          ═══════════════════════════════════════ */}
      <section className="section">
        <div className="container">
          <Reveal>
            <div className="section-intro">
              <div className="eyebrow">The Portfolio</div>
              <h2 className="d-lg t-navy">Portfolio Brands</h2>
            </div>
          </Reveal>

          <div className="grid-2" style={{ gap: 'var(--sp-6)' }}>
            {BRANDS.map((brand, i) => (
              <BrandCard key={brand.name} {...brand} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════
          SHARED SERVICES
          ═══════════════════════════════════════ */}
      <section className="section section--dark">
        <div className="container">
          <Reveal>
            <div className="section-intro section-intro--center">
              <div className="eyebrow eyebrow--center eyebrow--light">Infrastructure</div>
              <h2 className="d-lg t-white">Shared Services Across the Portfolio</h2>
              <p className="lead lead--white" style={{ maxWidth: '600px', margin: '0 auto' }}>
                Every portfolio brand benefits from centralized operational functions
                maintained at the holding company level — reducing cost, improving
                consistency, and ensuring institutional-grade oversight.
              </p>
            </div>
          </Reveal>

          <div className="grid-3" style={{ marginTop: 'var(--sp-12)' }}>
            {SHARED_SERVICES.map(({ title, body }, i) => (
              <Reveal key={title} delay={i * 0.07}>
                <div className="card card--dark" style={{ height: '100%' }}>
                  <h3 className="card__title">{title}</h3>
                  <p className="card__body">{body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════
          STRATEGIC OVERSIGHT
          ═══════════════════════════════════════ */}
      <section className="section">
        <div className="container">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'clamp(2rem, 5vw, 5rem)', alignItems: 'start' }}>

            <Reveal>
              <div className="eyebrow">Strategic Oversight</div>
              <h2 className="d-lg t-navy mb-8">
                One Governance Framework. Every Brand.
              </h2>
              <p className="lead mb-6">
                Each brand in the portfolio receives the same standard of governance
                regardless of size. Performance reporting, legal review, financial
                controls, and risk management are applied consistently — because
                governance gaps compound just as quickly as governance strengths.
              </p>
              <p className="lead">
                This consistency is what makes the portfolio acquisition-ready, partner-ready,
                and resilient — not just today, but as the portfolio grows and the demands
                on our operating model intensify.
              </p>
            </Reveal>

            <Reveal delay={0.12}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-5)' }}>
                {[
                  ['Performance Standards',  'All brands operate against defined KPIs, reported on a regular cadence.'],
                  ['Brand Autonomy',         'Each brand maintains its own voice, audience relationship, and market identity.'],
                  ['Holding Oversight',      'Driven 2 LLC provides governance, capital stewardship, and strategic guidance.'],
                  ['Integration Playbooks',  'New brands onboard through a structured integration process with documented milestones.'],
                  ['Exit Readiness',         'Portfolio disciplines are maintained with the expectation that any brand could be independently evaluated at any time.'],
                ].map(([title, desc]) => (
                  <div key={title} style={{ display: 'flex', gap: 'var(--sp-4)', alignItems: 'flex-start', paddingBottom: 'var(--sp-5)', borderBottom: '1px solid var(--gray-200)' }}>
                    <div style={{ width: 8, height: 8, background: 'var(--gold-500)', flexShrink: 0, marginTop: 7 }} />
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 'var(--text-sm)', color: 'var(--navy-800)', marginBottom: 'var(--sp-1)' }}>{title}</div>
                      <div style={{ fontSize: 'var(--text-sm)', color: 'var(--gray-600)', lineHeight: 1.75 }}>{desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </Reveal>

          </div>
        </div>
      </section>

      <CTASection
        eyebrow="Portfolio Inquiries"
        headline="Interested in the Portfolio?"
        subtext="For acquisition discussions, brand partnerships, or strategic inquiries about specific brands, contact us directly. We engage with substantive counterparts."
        primaryCTA={{ label: 'Contact Driven 2', to: '/contact' }}
        secondaryCTA={{ label: 'Operating Model', to: '/operating-model' }}
        variant="dark"
      />
    </>
  );
}
